import { EFinancingProgram } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { ControlledNumberField, ControlledTextField } from '@if/mui'
import { Box, Grid, Typography } from '@mui/material'
import { FinancingConfigDto } from '@src/data/api/financing-config-api'
import { FC } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MerchantFinancingProgramConfigsSchemaType } from './MerchantFinancingProgramConfigsPanel'

interface FinancingProgramConfigsInputsProps {
  financingProgramId: EFinancingProgram
  control: Control<MerchantFinancingProgramConfigsSchemaType>
  financingConfig: FinancingConfigDto | undefined
  disabled: boolean
  effectiveMaxLoanAmount: number
}

const FinancingProgramConfigsInputs: FC<FinancingProgramConfigsInputsProps> = ({
  financingProgramId,
  control,
  financingConfig,
  disabled,
  effectiveMaxLoanAmount,
}) => {
  const { t } = useTranslation()

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" color={disabled ? 'textDisabled' : 'textPrimary'} gutterBottom paddingBottom={1}>
        {t(`EFinancingProgram.${financingProgramId}`, { ns: 'enum' })}
      </Typography>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ControlledNumberField
            name={`financingProgramConfigs.${financingProgramId}.minLoanAmount`}
            control={control}
            label={t('financingProgramConfigs.minLoanAmount')}
            fullWidth
            placeholder={financingConfig ? String(financingConfig.minimumLoanAmount) : undefined}
            disabled={disabled}
            isMoney
          />
          <Typography variant="caption" color="textDisabled" gutterBottom>
            {t('financingProgramConfigs.programDefault')}: {formatCurrency(financingConfig?.minimumLoanAmount)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ControlledNumberField
            name={`financingProgramConfigs.${financingProgramId}.maxLoanAmount`}
            control={control}
            label={t('financingProgramConfigs.maxLoanAmount')}
            fullWidth
            placeholder={String(effectiveMaxLoanAmount)}
            disabled={disabled}
            isMoney
          />
          <Typography variant="caption" color="textDisabled" gutterBottom>
            {t('financingProgramConfigs.programDefault')}: {formatCurrency(effectiveMaxLoanAmount)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ControlledTextField
            name={`financingProgramConfigs.${financingProgramId}.maxLoanTerm`}
            control={control}
            label={`${t('financingProgramConfigs.maxLoanTerm')} (${t('common.months')})`}
            fullWidth
            placeholder={financingConfig ? String(financingConfig.maxLoanTerm) : undefined}
            disabled={disabled}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
          <Typography variant="caption" color="textDisabled" gutterBottom>
            {t('financingProgramConfigs.programDefault')}: {financingConfig?.maxLoanTerm} {t('common.months')}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  )
}

export default FinancingProgramConfigsInputs
